
import { MsalProvider } from "@azure/msal-react";
import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from "react-router-dom";
import {
	publicClientApplication,
	scopes,
} from "./authentication/PublicClientApplication";
import LoadingScreen from "./components/LoadingScreen";
import './index.css';
import router from './router/index.tsx';
import { DataService } from "./services/dataService";
import useConfigStore from "./store/config";

// Create root once, reuse for all renders
const rootElement = document.getElementById('root')!;
const root = ReactDOM.createRoot(rootElement);

// Helper function with retry mechanism
async function fetchWithRetry<T>(
	fetchFn: () => Promise<T>,
	maxRetries: number = 3,
	delayMs: number = 1000,
	operationName: string = 'operation'
): Promise<T> {
	let lastError: Error | null = null;
	for (let i = 0; i < maxRetries; i++) {
		try {
			console.log(`📡 ${operationName} attempt ${i + 1}/${maxRetries}...`);
			return await fetchFn();
		} catch (error) {
			lastError = error instanceof Error ? error : new Error(String(error));
			console.warn(`⚠️ ${operationName} failed on attempt ${i + 1}: ${lastError.message}`);
			if (i < maxRetries - 1) {
				console.log(`⏳ Retrying in ${delayMs}ms...`);
				await new Promise(resolve => setTimeout(resolve, delayMs));
				delayMs *= 2; // Exponential backoff
			}
		}
	}
	throw lastError || new Error(`${operationName} failed after ${maxRetries} attempts`);
}

// Helper function to initialize app with user data
async function initializeAppWithUser() {
	const accounts = publicClientApplication.getAllAccounts();
	console.log('🔐 Available accounts:', accounts.length);

	if (accounts.length === 0) {
		console.error('❌ No accounts available even after auth result');
		root.render(
			<LoadingScreen message="Authentication failed: No accounts available.\n\nPlease refresh the page." />
		);
		return;
	}

	const account = accounts[0];

	try {
		// Pre-acquire token
		try {
			await publicClientApplication.acquireTokenSilent({
				scopes,
				account,
			});
			console.log('✅ Token pre-acquired successfully');
		} catch (error) {
			console.log('⚠️ Token pre-acquisition failed, will retry:', error);
		}

		// Fetch user info before rendering
		console.log('📋 Initializing user data...');
		const dataService = new DataService(publicClientApplication, account);

		// Fetch user info with retry logic
		const userInfoData = await fetchWithRetry(
			() => dataService.getUserInfo(),
			3,
			1000,
			'User info fetch'
		);
		console.log('✅ User info fetched:', userInfoData);

		// Fetch user roles
		const userUpn = account.username || '';
		const rolesResponse = await dataService.getUserAppPermissions(userUpn, '2');

		let finalRoles = ['User'];
		if (rolesResponse && rolesResponse.length > 0) {
			const fetchedRoles = rolesResponse.map((perm: { rolle: string; appPart: string }) => perm.rolle);
			finalRoles = [...['User'], ...fetchedRoles];
			console.log('✅ Roles fetched:', finalRoles);

			// Store personal parts
			const personalParts = rolesResponse.map((perm: { rolle: string; appPart: string }) => perm.appPart);
			sessionStorage.setItem('personalParts', JSON.stringify(personalParts));
		}

		// Store roles in sessionStorage for Headerbar to use
		sessionStorage.setItem('userPersonalRole', JSON.stringify(finalRoles));

		// Update config store
		useConfigStore.setState({
			userConfig: {
				userInfo: userInfoData,
			},
			roleConfig: {
				availableRoles: finalRoles,
				selectedRole: finalRoles.length > 0 ? finalRoles[0] : null,
			},
		});

		console.log('✅ Initialization complete');

		// Render the app after successful initialization
		console.log('🎨 Rendering main app...');
		root.render(
			<React.StrictMode>
				<MsalProvider instance={publicClientApplication}>
					<RouterProvider router={router} />
				</MsalProvider>
			</React.StrictMode>
		);
		console.log('🎨 App rendered successfully');
	} catch (initError) {
		const errorMsg = initError instanceof Error ? initError.message : String(initError);
		console.error('❌ Initialization error:', errorMsg);

		// Check if this is an Edge CORS issue
		const isEdgeCorsIssue = errorMsg.includes('CORS') || errorMsg.includes('local address space') || errorMsg.includes('ERR_FAILED');
		const isEdgeBrowser = /Edg\//.test(navigator.userAgent);

		let displayMessage = `Initialization failed: ${errorMsg}\n\nPlease refresh the page to retry.`;
		if (isEdgeBrowser && isEdgeCorsIssue) {
			displayMessage = `Network connection issue (Edge browser detected).\n\nTry:\n1. Refresh this page\n2. Clear browser cache\n3. Use Chrome or Firefox as alternative\n\nError: ${errorMsg}`;
		}

		// Show error and offer retry
		root.render(
			<LoadingScreen message={displayMessage} />
		);
	}
}

publicClientApplication.initialize().then(async () => {
	console.log('🔐 MSAL initialized');
	// Show loading screen immediately
	root.render(<LoadingScreen message="Authenticating..." />);

	try {
		console.log('🔐 Checking redirect promise...');

		// Add a timeout to detect if handleRedirectPromise hangs
		const redirectPromise = publicClientApplication.handleRedirectPromise();
		const timeoutPromise = new Promise((_, reject) =>
			setTimeout(() => reject(new Error('handleRedirectPromise timeout after 5 seconds')), 5000)
		);

		const authenticationResult = await Promise.race([redirectPromise, timeoutPromise]);
		console.log('🔐 handleRedirectPromise result:', authenticationResult ? '✅ Auth Result' : '⊘ null');

		if (authenticationResult === null) {
			const accounts = publicClientApplication.getAllAccounts();
			console.log('🔐 Available accounts:', accounts.length);
			if (accounts.length > 0) {
				// We have existing accounts, initialize the app
				console.log('🔐 Using existing account');
				await initializeAppWithUser();
			} else {
				// No accounts, redirect to login
				console.log('🔐 No accounts found, redirecting to login...');
				try {
					await publicClientApplication.loginRedirect({ scopes });
					console.log('🔐 loginRedirect called - redirecting to Azure AD');
				} catch (loginError) {
					console.error('🔐 loginRedirect error:', loginError);
					root.render(
						<LoadingScreen message={`Login redirect failed.\n\nError: ${loginError instanceof Error ? loginError.message : String(loginError)}`} />
					);
				}
				return;
			}
		} else {
			// User just logged in via Azure redirect, initialize the app
			console.log('🔐 Auth result received (post-Azure redirect) - initializing app...');
			await initializeAppWithUser();
		}
	} catch (error) {
		console.error('❌ Critical authentication error:', error);
		console.error('❌ Error details:', error instanceof Error ? error.stack : String(error));
		const errorMsg = error instanceof Error ? error.message : String(error);
		root.render(
			<LoadingScreen message={`Authentication failed: ${errorMsg}\n\nPlease refresh the page.`} />
		);
	}
}).catch((error) => {
	console.error('❌ MSAL initialization promise rejected:', error);
	root.render(
		<LoadingScreen message={`MSAL initialization failed.\n\nError: ${error instanceof Error ? error.message : String(error)}`} />
	);
});
